/**
 * ListWidget - Dynamic list display widget with inline item editor
 *
 * Shows a list of items that can be added/removed by voice.
 * In the builder, shows preview items with inline editing.
 * At runtime, binds to app state.
 *
 * When selected, shows:
 * - Editable title/subtitle for each item
 * - Delete button per item
 * - "Add Item" button
 * - Layout selector (vertical/horizontal/grid)
 * - Voice settings panel
 */

import { memo, useState, useEffect } from 'react';
import type { ListWidget as ListWidgetType } from '@deep-agents/shared-types';
import type { WidgetComponentProps } from '../types';
import { WIDGET_PADDING } from '../constants';
import { VoiceConfigEditor } from './VoiceConfigEditor';

interface ListWidgetProps extends Omit<WidgetComponentProps, 'widget'> {
  widget: ListWidgetType;
}

export const ListWidgetComponent = memo(function ListWidgetComponent({
  widget,
  isSelected,
  onSelect,
  onUpdate,
}: ListWidgetProps) {
  const { props, previewItems = [], voice } = widget;
  const layout = props.layout || 'vertical';

  // Local editing state - mirrors previewItems for in-place editing
  const [editingItems, setEditingItems] = useState<Array<{ title: string; subtitle?: string }>>(
    previewItems.length > 0 ? previewItems : []
  );

  // Sync local state if parent data changes (e.g., undo/redo)
  useEffect(() => {
    setEditingItems(previewItems.length > 0 ? previewItems : []);
  }, [previewItems]);

  // Use editing items for display, or show placeholders if empty
  const displayItems = editingItems.length > 0 ? editingItems : [
    { title: 'Item 1', subtitle: 'Tap to edit' },
    { title: 'Item 2', subtitle: 'Tap to edit' },
  ];

  // --- Item editing helpers ---

  /** Update a single field on one item and save immediately */
  const updateItem = (index: number, field: 'title' | 'subtitle', value: string) => {
    const updated = [...editingItems];
    updated[index] = { ...updated[index], [field]: value || undefined };
    setEditingItems(updated);
  };

  /** Save current editing items to widget data */
  const saveItems = () => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      previewItems: editingItems.filter((item) => item.title.trim() !== ''),
    });
  };

  /** Add a new empty item at the end */
  const addItem = () => {
    const updated = [...editingItems, { title: '', subtitle: '' }];
    setEditingItems(updated);
    // Don't save yet - user needs to type a title first
  };

  /** Remove an item by index */
  const removeItem = (index: number) => {
    const updated = editingItems.filter((_, i) => i !== index);
    setEditingItems(updated);
    // Save immediately since this is a destructive action
    if (onUpdate) {
      onUpdate({
        ...widget,
        previewItems: updated.filter((item) => item.title.trim() !== ''),
      });
    }
  };

  /** Handle layout change */
  const handleLayoutChange = (newLayout: 'vertical' | 'horizontal' | 'grid') => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      props: { ...widget.props, layout: newLayout },
    });
  };

  /** Handle voice config change */
  const handleVoiceChange = (voiceConfig: typeof voice) => {
    if (!onUpdate) return;
    onUpdate({
      ...widget,
      voice: voiceConfig,
    });
  };

  return (
    <div
      className={`
        relative rounded-xl transition-all duration-200 cursor-pointer
        bg-white border border-gray-200
        ${isSelected ? 'ring-2 ring-violet-500 ring-offset-2' : 'hover:border-gray-300'}
      `}
      style={{ padding: WIDGET_PADDING }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      {/* List header */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
          List
        </span>
        <span className="text-xs text-gray-400">
          {editingItems.length > 0 ? editingItems.length : displayItems.length} items
        </span>
      </div>

      {/* List items preview */}
      <div
        className={`
          ${layout === 'vertical' ? 'flex flex-col gap-2' : ''}
          ${layout === 'horizontal' ? 'flex flex-row gap-2 overflow-x-auto' : ''}
          ${layout === 'grid' ? 'grid grid-cols-2 gap-2' : ''}
        `}
      >
        {displayItems.map((item, index) => (
          <div
            key={index}
            className={`
              px-3 py-2 rounded-lg bg-gray-50 border border-gray-100
              ${layout === 'horizontal' ? 'flex-shrink-0 min-w-[120px]' : ''}
            `}
          >
            <div className="text-sm font-medium text-gray-800">{item.title}</div>
            {item.subtitle && (
              <div className="text-xs text-gray-500 mt-0.5">{item.subtitle}</div>
            )}
          </div>
        ))}
      </div>

      {/* Empty state */}
      {editingItems.length === 0 && props.emptyMessage && (
        <div className="text-center text-gray-400 text-sm py-4">
          {props.emptyMessage}
        </div>
      )}

      {/* ─── INLINE EDITOR (shown when selected) ─── */}
      {isSelected && (
        <div
          className="mt-3 pt-3 border-t border-gray-200 space-y-3"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Layout selector */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] font-medium text-gray-500">Layout:</span>
            {(['vertical', 'horizontal', 'grid'] as const).map((l) => (
              <button
                key={l}
                type="button"
                onClick={() => handleLayoutChange(l)}
                className={`
                  nodrag nopan px-2 py-0.5 text-[10px] rounded border transition-colors
                  ${layout === l
                    ? 'bg-violet-100 text-violet-700 border-violet-300'
                    : 'bg-white text-gray-500 border-gray-200 hover:border-violet-200'
                  }
                `}
              >
                {l.charAt(0).toUpperCase() + l.slice(1)}
              </button>
            ))}
          </div>

          {/* Editable item list */}
          <div className="space-y-2">
            <span className="text-[10px] font-medium text-gray-500">Items:</span>
            {editingItems.map((item, idx) => (
              <div key={idx} className="flex gap-2 items-start">
                {/* Item number indicator */}
                <span className="text-[10px] text-gray-400 mt-2 w-4 text-right flex-shrink-0">
                  {idx + 1}.
                </span>

                {/* Title + subtitle inputs */}
                <div className="flex-1 space-y-1">
                  <input
                    type="text"
                    value={item.title}
                    onChange={(e) => updateItem(idx, 'title', e.target.value)}
                    onBlur={saveItems}
                    placeholder="Item title"
                    className="nodrag nopan w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 bg-white"
                  />
                  <input
                    type="text"
                    value={item.subtitle || ''}
                    onChange={(e) => updateItem(idx, 'subtitle', e.target.value)}
                    onBlur={saveItems}
                    placeholder="Subtitle (optional)"
                    className="nodrag nopan w-full px-2 py-1 text-xs text-gray-500 border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-500 bg-white"
                  />
                </div>

                {/* Delete button */}
                <button
                  type="button"
                  onClick={() => removeItem(idx)}
                  className="nodrag nopan mt-1 p-1 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                  title="Remove item"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}

            {/* Add item button */}
            <button
              type="button"
              onClick={addItem}
              className="nodrag nopan w-full flex items-center justify-center gap-1 px-3 py-1.5 text-xs text-violet-600 border border-dashed border-violet-300 rounded-lg hover:bg-violet-50 transition-colors"
            >
              <span>+</span>
              <span>Add Item</span>
            </button>
          </div>

          {/* Voice settings (expandable) */}
          <VoiceConfigEditor config={voice} onChange={handleVoiceChange} />
        </div>
      )}

      {/* Voice commands indicator (shown when NOT editing) */}
      {!isSelected && voice?.speakable && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="flex items-center gap-1 text-[10px] text-violet-600">
            <span>🗣️</span>
            <span>Voice: "Add item", "Remove item"</span>
          </div>
        </div>
      )}
    </div>
  );
});
